/**
 * Groups service — member listing, add/kick/ban/unban.
 * Ported 1:1 from the legacy flat services/groups.js. Response shapes verified
 * against docs/groups-migration/Api-response/.
 *
 * NOTE (D5): this module intentionally overlaps with src/services/users/groups.ts
 * (kick/ban/add-member all exist there too, for the Users page's "Groups" tab).
 * That module is user-scoped (single uid mutating its own membership set) and
 * already shipped/tested as part of the completed Users migration — it is
 * deliberately NOT reused or modified here. This module is group-scoped (the
 * Groups feature managing a specific group's member list) and has its own
 * calling convention. See GROUPS-MIGRATION-PLAN.md decision D5.
 */
import constants from '../../constants'
import { getChatApiBase, getChatApiHeaders } from '../chatApiClient'
import axios from '../axiosInstance'
import {
  ChatApp,
  GroupMembersListResponse,
  GroupMembersChangeResponse,
  SuccessMessageResponse,
} from './types'

export async function members(
  id: string,
  app: ChatApp,
  apiHost: string,
  pageNo = 1,
  searchText = '',
): Promise<GroupMembersListResponse> {
  const URL = `${getChatApiBase(app, apiHost)}/groups/${id}/members?per_page=${
    constants.perPageInListings
  }&page=${pageNo}&searchKey=${searchText}&useCursoredIndex=1`
  return axios
    .get(`${URL}`, {
      headers: getChatApiHeaders(app),
    })
    .then(res => res.data)
    .catch(error => error?.response?.data ?? Promise.reject(error))
}

export async function searchMembers(
  id: string,
  searchText: string,
  app: ChatApp,
  apiHost: string,
  pageNo = 1,
): Promise<GroupMembersListResponse> {
  const URL = `${getChatApiBase(app, apiHost)}/groups/${id}/members?searchKey=${searchText}&per_page=${
    constants.perPageInListings
  }&page=${pageNo}&useCursoredIndex=1`
  return axios
    .get(`${URL}`, {
      headers: getChatApiHeaders(app),
    })
    .then(res => res.data)
    .catch(error => error?.response?.data ?? Promise.reject(error))
}

// NOTE: no `nonParticipants` function here. Confirmed by reading
// redux/apps/sagas.js's NON_PARTICIPANTS_LIST handler (L13548) directly: the
// add-members modal's source list is NOT a group-scoped "non-members"
// endpoint — it calls the generic `services/users.list` (a plain user
// search) and the UI filters out existing members itself. An earlier draft
// of this file guessed at a `/groups/{guid}/members?onlyNonMembers=1`
// endpoint; that was unverified and turned out to be wrong, so it was
// removed rather than shipped. Phase 3's redux slice should call
// `services/users`'s `list`/`search`, not anything from this module, for
// the add-members search.

export async function kickMember(
  uid: string,
  guid: string,
  app: ChatApp,
  apiHost: string,
): Promise<SuccessMessageResponse> {
  const URL = `${getChatApiBase(app, apiHost)}/groups/${guid}/members/${uid}`
  return axios
    .delete(`${URL}`, {
      headers: getChatApiHeaders(app),
    })
    .then(res => res.data)
    .catch(error => error?.response?.data ?? Promise.reject(error))
}

// NOTE: no `changeMemberScope` function here. Confirmed by reading
// redux/apps/sagas.js's CHANGE_MEMBER_SCOPE handler (L8588) directly: it
// imports `updateScopeInGroup` from `services/users` (services/users/groups.ts)
// and reuses it as-is — POST to `/groups/{guid}/members` with
// `{admins:[uid]}`/`{moderators:[uid]}`/`{participants:[uid]}` selecting the
// scope, NOT a PUT to `/groups/{guid}/members/{uid}` with `{scope}`. An
// earlier draft of this file guessed at the latter; that was unverified and
// turned out to be wrong, so it was removed rather than shipped. Per D5,
// `services/users/groups.ts` is not modified — but its `updateScopeInGroup`
// export IS the real, live implementation for this action, so Phase 3's
// redux slice should import and reuse it directly, matching production.

export async function banMember(
  uid: string,
  guid: string,
  app: ChatApp,
  apiHost: string,
): Promise<SuccessMessageResponse> {
  const URL = `${getChatApiBase(app, apiHost)}/groups/${guid}/bannedusers/${uid}`
  return axios
    .post(
      `${URL}`,
      {},
      {
        headers: getChatApiHeaders(app),
      },
    )
    .then(res => res.data)
    .catch(error => error?.response?.data ?? Promise.reject(error))
}

export async function unbanMember(
  uid: string,
  guid: string,
  app: ChatApp,
  apiHost: string,
): Promise<SuccessMessageResponse> {
  const URL = `${getChatApiBase(app, apiHost)}/groups/${guid}/bannedusers/${uid}`
  return axios
    .delete(`${URL}`, {
      headers: getChatApiHeaders(app),
    })
    .then(res => res.data)
    .catch(error => error?.response?.data ?? Promise.reject(error))
}

export async function getBanMembers(
  guid: string,
  app: ChatApp,
  apiHost: string,
  pageNo = 1,
  searchText = '',
): Promise<GroupMembersListResponse> {
  const URL = `${getChatApiBase(app, apiHost)}/groups/${guid}/bannedusers?per_page=${
    constants.perPageInListings
  }&page=${pageNo}&searchKey=${searchText}&useCursoredIndex=1`
  return axios
    .get(`${URL}`, {
      headers: getChatApiHeaders(app),
    })
    .then(res => res.data)
    .catch(error => error?.response?.data ?? Promise.reject(error))
}

export async function searchBanMembers(
  guid: string,
  searchText: string,
  app: ChatApp,
  apiHost: string,
  pageNo = 1,
): Promise<GroupMembersListResponse> {
  const URL = `${getChatApiBase(app, apiHost)}/groups/${guid}/bannedusers?searchKey=${searchText}&per_page=${
    constants.perPageInListings
  }&page=${pageNo}&useCursoredIndex=1`
  return axios
    .get(`${URL}`, {
      headers: getChatApiHeaders(app),
    })
    .then(res => res.data)
    .catch(error => error?.response?.data ?? Promise.reject(error))
}

/** Bulk add — verified: response is a per-uid result map, not a flat
 *  success/fail. "Already a member" is a 200 with a per-uid failure flag
 *  (`data.participants[uid].success === false`), not an HTTP error. */
export async function addMembers(
  uids: string[],
  guid: string,
  app: ChatApp,
  apiHost: string,
): Promise<GroupMembersChangeResponse> {
  const URL = `${getChatApiBase(app, apiHost)}/groups/${guid}/members`
  return axios
    .post(
      `${URL}`,
      { participants: uids },
      {
        headers: getChatApiHeaders(app),
      },
    )
    .then(res => res.data)
    .catch(error => error?.response?.data ?? Promise.reject(error))
}
